import { colors, Flex, Grid, Heading, HStack, LinkBox, LinkOverlay, Text } from "@biblioteksentralen/react";
import { Publication, PublicationListItem } from "@libry-content/types";
import { getPublicationUrlDeprecated } from "../../../utils/getPublicationUrl";
import { usePublicationLangAttribute } from "../../../utils/hooks/usePublicationLangAttribute";
import CoverImageWithPlaceholder from "../CoverImageWithPlaceholder";
import { useCommonData } from "../../layout/CommonDataProvider";
import { MediaTypeTags } from "../MediaTypesAndTargetAudiences";
import { useTranslation } from "../../../utils/hooks/useTranslation";

interface Props {
  item: Publication | PublicationListItem;
  headingSize?: "h2" | "h3";
  showPublicationTeaser?: boolean;
}

const PublicationView = ({ item, headingSize, showPublicationTeaser }: Props) => {
  const { ts } = useTranslation();
  const publication = item._type === "publication" ? item : item.publication;
  const [title] = publication?.title?.split(":") ?? [];
  const { site } = useCommonData();
  const publicationUrl = getPublicationUrlDeprecated({ publication, work: undefined, site });
  const langAttribute = usePublicationLangAttribute(publication);

  return (
    <LinkBox>
      <Grid
        templateColumns="2fr 3fr"
        role="group"
        gap="1rem"
        position="relative"
        alignItems="flex-end"
        // Denne viser en linje mellom hver rad av elementer i gridet. https://geary.co/internal-borders-css-grid
        sx={{
          "::before, ::after": {
            content: "''",
            position: "absolute",
            backgroundColor: colors.grey15,
          },
          "::before": {
            inlineSize: "100vw",
            blockSize: "1px",
            insetBlockEnd: -6,
          },
        }}
      >
        {publication && <CoverImageWithPlaceholder publication={publication} resolution={300} showFavoriteButton />}
        <Flex flexDir="column" minHeight="4.3rem" alignSelf="end" gap="0.2rem" justifyContent="flex-end">
          <HStack flexWrap="wrap">
            <MediaTypeTags mediaTypes={publication?.mediaTypes} />
          </HStack>
          {/** using nofollow to stop bots from indexing api route (will not be necessary when we use works instead of publications) */}
          <LinkOverlay href={publicationUrl} _groupHover={{ textDecoration: "underline" }} rel="nofollow">
            <Heading as={headingSize} size="sm" noOfLines={3} lang={langAttribute}>
              {title}
            </Heading>
          </LinkOverlay>
          <Text fontSize="sm" decoration="underline">
            {publication?.author}
          </Text>
          {showPublicationTeaser && item && item._type == "publicationListItem" && (
            <Text fontSize="sm">{ts(item.contextualTeaser)}</Text>
          )}
        </Flex>
      </Grid>
    </LinkBox>
  );
};

export default PublicationView;
